import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";

interface TemplateChangeConfirmModalProps {
  isOpen: boolean;
  templateName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const TemplateChangeConfirmModal: React.FC<TemplateChangeConfirmModalProps> = ({
  isOpen,
  templateName,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <MUIUnifiedModal open={isOpen} onClose={onCancel} width="480px">
      <ContentWrapper>
        <IconWrapper>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </IconWrapper>

        <Title>{t("treatment.editor.template_change.title")}</Title>

        <Message>
          <Trans
            t={t}
            i18nKey="treatment.editor.template_change.body"
            components={{ TemplateName: <TemplateName /> }}
            values={{ name: templateName }}
          />
        </Message>

        <ButtonGroup>
          <ConfirmButton onClick={onConfirm}>{t("treatment.editor.template_change.confirm")}</ConfirmButton>
          <CancelButton onClick={onCancel}>{t("buttons.cancel")}</CancelButton>
        </ButtonGroup>
      </ContentWrapper>
    </MUIUnifiedModal>
  );
};

const ContentWrapper = styled.div`
  padding: 0 32px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #fff4e6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  svg {
    font-size: 32px;
    color: #ff9500;
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #1a2332;
  margin: 0 0 16px 0;
`;

const Message = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #5c6f82;
  margin: 0 0 32px 0;
  max-width: 380px;
  strong {
    color: #0066cc;
    font-weight: 600;
  }
`;

const TemplateName = styled.span`
  font-weight: 600;
  color: #0066cc;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ConfirmButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  background-color: #0066cc;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #0052a3;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CancelButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  background-color: #f8f9fa;
  color: #5c6f82;
  font-size: 15px;
  font-weight: 600;
  border: ${({ theme }) => theme.semantic.container.border};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e1e8ef;
    color: #1a2332;
  }

  &:active {
    transform: scale(0.98);
  }
`;
